import { useEffect } from "react";
import useRoomToken from "../useRoomToken";
import { useRoom } from "@livekit/react-core";
import { ConnectionState, RoomOptions } from "livekit-client";
import { useSelectedMediaDevice } from "../useMediaDeviceSelector";

// Add types for the codec preferences
interface ExtendedRTCRtpTransceiver extends RTCRtpTransceiver {
  setCodecPreferences(codecs: RTCRtpCodecCapability[]): void;
}

// Patch RTCRtpTransceiver to prevent codec preference errors
const prototype = RTCRtpTransceiver.prototype as ExtendedRTCRtpTransceiver;
const originalSetCodecPreferences = prototype.setCodecPreferences;
if (originalSetCodecPreferences) {
  prototype.setCodecPreferences = function (codecs: RTCRtpCodecCapability[]) {
    try {
      return originalSetCodecPreferences.call(this, codecs);
    } catch (e) {
      console.warn("Codec preference error suppressed:", e);
      return Promise.resolve();
    }
  };
}

export default function useLiveKit(roomId: string) {
  const [selectedVideoDevice] = useSelectedMediaDevice("videoinput");
  const [selectedAudioDevice] = useSelectedMediaDevice("audioinput");
  const { roomToken } = useRoomToken(roomId);
  const roomOptions: RoomOptions = {
    adaptiveStream: true,
    dynacast: true,
    audioCaptureDefaults: selectedAudioDevice
      ? {
          deviceId: selectedAudioDevice.deviceId
        }
      : undefined,
    videoCaptureDefaults: selectedVideoDevice
      ? {
          deviceId: selectedVideoDevice.deviceId
        }
      : undefined
  };
  const {
    connect,
    room,
    participants,
    isConnecting,
    error,
    connectionState
  } = useRoom(roomOptions);

  // eslint-disable-next-line no-console
  console.log({ livekit: { isConnecting, connectionState, error } });

  useEffect(() => {
    const run = async () => {
      if (roomToken) {
        // initiate connection to the livekit room
        await connect("wss://sfu.beyondmoore.ai", roomToken);
        // request camera and microphone permissions and publish tracks
        await room!.localParticipant.enableCameraAndMicrophone();
      }
    };
    run();
  }, [roomToken]);

  useEffect(() => {
    const updateDevices = async () => {
      if (room && connectionState === ConnectionState.Connected) {
        if (selectedAudioDevice) {
          await room.switchActiveDevice(
            "audioinput",
            selectedAudioDevice.deviceId
          );
        }
        if (selectedVideoDevice) {
          await room.switchActiveDevice(
            "videoinput",
            selectedVideoDevice.deviceId
          );
        }
      }
    };
    updateDevices();
  }, [selectedAudioDevice, selectedVideoDevice]);

  return {
    room,
    participants: Object.fromEntries(participants.map((p) => [p.identity, p]))
  };
}
